import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdOutlineExplore,
  MdOutlineManageSearch,
  MdOutlineBuild,
  MdOutlineHandyman,
  MdOutlineTranslate,
  MdOutlineTrendingUp,
  MdOutlineAdd,
  MdOutlineRemove,
} from 'react-icons/md';
import SeoHead from '../components/SeoHead';

const MEETING_LINK = 'https://meetings-eu1.hubspot.com/claus-moeldrup';

const finderResults = {
  startpakke: {
    label: 'Vi anbefaler: AI-kursus',
    text: 'Et to-timers kursus giver jeres team et fælles sprog og konkrete færdigheder, uden at I behøver vide noget om AI på forhånd. Det er den laveste risiko og højeste læringseffekt for organisationer i jeres situation.',
  },
  coe: {
    label: 'Vi anbefaler: AI Center of Excellence',
    text: 'Et struktureret kapabilitetsprogram der tager jer fra nysgerrige til kompetente, med AI-strategi, færdige løsninger og organisatorisk forankring. Vælg det track der passer jeres modenhed og ambition.',
  },
  agents: {
    label: 'Vi anbefaler: AI Agents',
    text: 'Har I et konkret use case, er I klar til at implementere. Kontakt os, og vi afdækker hurtigt scope, tidsplan og budget. Det første møde er gratis og uforpligtende.',
  },
};

const finderOptions = [
  {
    id: 'startpakke',
    Icon: MdOutlineExplore,
    title: 'Vi er nysgerrige, men usikre',
    desc: 'Vi taler om AI, men ved ikke rigtig, hvor vi skal starte.',
  },
  {
    id: 'coe',
    Icon: MdOutlineManageSearch,
    title: 'Vi vil vide, hvad AI kan gøre for os',
    desc: 'Vi tror der er potentiale, men mangler et konkret grundlag.',
  },
  {
    id: 'agents',
    Icon: MdOutlineBuild,
    title: 'Vi er klar til at bygge',
    desc: 'Vi har et konkret use case og vil have det til at virke.',
  },
];

const offerings = [
  {
    id: 'startpakke',
    tag: 'Undervisning',
    title: 'AI-kursus',
    outcome: 'Jeres team forstår AI, kan bruge det konkret og går hjem med færdigheder de kan bruge næste dag. På bare 2 timer.',
    price: 'Fra 10.500 DKK',
    billing: 'Workshop for teams · Individuel coaching',
    fits: [
      'I vil have hele teamet med, ikke kun de tech-nørder',
      'I vil gå fra "vi snakker om AI" til "vi bruger AI"',
      'I har brug for en hurtig, lavrisiko indgang',
    ],
    highlights: [
      'Kick-off: AI i jeres kontekst',
      'Cases og live-demonstrationer',
      'Hands-on øvelser i prompts',
      'Opsamling og konkrete anbefalinger',
    ],
    ctaLabel: 'Læs mere om kurset',
    ctaRoute: '/ydelser/ai-kursus',
    featured: false,
  },
  {
    id: 'coe',
    tag: 'Kapabilitetsprogram',
    title: 'AI Center of Excellence',
    outcome: 'Byg varig AI-kapabilitet i organisationen. Fra de første assistenter til et fuldt internt CoE-team.',
    price: 'Fra 40.000 kr/måned',
    billing: '3 niveauer, tilpasset jeres modenhed og ambition',
    fits: [
      'I vil have mere end et enkeltprojekt, I vil bygge kapabilitet',
      'I skal have ledelsen og organisationen med, ikke kun IT',
      'I tænker langsigtet og vil have en struktureret vej frem',
    ],
    highlights: [
      'AI-strategi, governance og roadmap',
      'Færdigudviklede AI-assistenter og workflows',
      'Kompetenceløft i hele organisationen',
      'Tre tracks — vælg intensitet der passer jer',
    ],
    ctaLabel: 'Se de tre tracks',
    ctaRoute: '/ydelser/ai-center-of-excellence',
    featured: true,
  },
  {
    id: 'agents',
    tag: 'Implementering',
    title: 'AI Agents',
    outcome: 'Vi bygger og driver skræddersyede AI-agenter, der løser konkrete opgaver i jeres drift, mens I fokuserer på andet.',
    price: 'Kontakt os for pris',
    billing: 'Vi tilpasser løsning og abonnement til jeres behov',
    fits: [
      'I har gentagne, manuelle processer I vil automatisere',
      'I vil have en løsning I ikke selv skal drifte og vedligeholde',
      'I er klar til at investere i varig effekt',
    ],
    highlights: [
      'Opsætning af multi-agent workflows',
      'Integration til jeres værktøjer og data',
      'Løbende overvågning, evaluering og forbedringer',
      'Månedlig statusrapport og optimering',
    ],
    ctaLabel: 'Fortæl os om jeres behov',
    ctaRoute: '/losninger',
    featured: false,
  },
];

const whyItems = [
  {
    Icon: MdOutlineHandyman,
    title: 'Vi bygger det selv',
    desc: 'Vi rådgiver ikke bare, vi bygger det også. Direkte i jeres platform og miljø, med samme hold fra første møde til levering.',
  },
  {
    Icon: MdOutlineTranslate,
    title: 'Ikke-teknisk sprog',
    desc: 'Vi oversætter AI til forretningssprog. Ingen jargon, kun konkrete resultater og tal.',
  },
  {
    Icon: MdOutlineTrendingUp,
    title: 'ROI fra dag ét',
    desc: 'Vi estimerer effekten, inden vi starter. Ingen overraskelser, kun dokumenterede resultater.',
  },
];

const faqs = [
  {
    q: 'Vi bruger allerede ChatGPT eller Copilot. Har vi brug for jer?',
    a: 'De fleste organisationer bruger kun 10–20% af potentialet i de AI-værktøjer de allerede har. Vi hjælper jer med at bruge dem rigtigt, og vurderer om der er processer, hvor skræddersyede løsninger giver mere værdi end standard-licenser.',
  },
  {
    q: 'Hvad kræver det af vores tid og ressourcer?',
    a: 'AI-kurset tager 2 timer og kræver ingen forberedelse fra jer. Discovery-forløbet kræver 1–2 interne interviews og en workshop på en halv dag. Agents-løsninger tilrettelægger vi, så belastningen på jeres team er minimal, typisk 2–3 timer i opstartsfasen.',
  },
  {
    q: 'Hvor hurtigt ser vi resultater?',
    a: 'Efter et AI-kursus bruger de fleste teams AI aktivt inden for den første uge. Discovery afleverer vi inden for 3–4 uger. Agenter er typisk i drift inden for 6–8 uger afhængigt af integration og kompleksitet.',
  },
  {
    q: 'Er vi for små til AI-agenter?',
    a: 'Nej. Vi arbejder med virksomheder fra 10 til 500 ansatte. Det vigtigste er ikke størrelsen, men om der er en gentagen opgave der kan automatiseres. Det finder vi ud af i et indledende møde, som er gratis og uden forpligtelse.',
  },
  {
    q: 'Hvad skal vi starte med, hvis vi er helt nye?',
    a: 'Typisk anbefaler vi at starte med et AI-kursus for at bygge forståelse og entusiasme i teamet, og derefter kortlægge de konkrete muligheder. Book et 20-minutters afklaringsmøde, og vi hjælper jer med at finde det rigtige udgangspunkt.',
  },
];

const Ydelser = () => {
  const navigate = useNavigate();
  const [activeFinder, setActiveFinder] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div style={pageStyle}>
      <SeoHead
        title="AICE Ydelser | AI Discovery, agenter og implementering"
        description="Se AICE's AI-ydelser: AI-kursus, AI Discovery og skræddersyede AI-agenter. Book en samtale og få et forløb tilpasset jeres organisation."
        canonicalPath="/ydelser"
      />

      {/* HERO */}
      <section style={heroSection}>
        <p style={eyebrow}>Ydelser</p>
        <h1 style={heroTitle}>AI, der giver <span style={{ color: '#C03626' }}>mærkbar effekt</span> i din virksomhed</h1>
        <p style={heroText}>
          Vi rådgiver, underviser og implementerer, alt efter hvor I er.
          Alle forløb ender med noget, I kan bruge fra dag ét.
        </p>
      </section>

      {/* CARDS */}
      <section style={cardsSection}>
        <div style={cardsGrid}>
          {offerings.map((offer) => (
              <article
                key={offer.id}
                style={{
                  ...card,
                  ...(offer.featured ? cardFeatured : {}),
                  position: 'relative',
                }}
                onClick={() => offer.ctaRoute ? navigate(offer.ctaRoute) : window.open(offer.ctaHref, '_blank')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && (offer.ctaRoute ? navigate(offer.ctaRoute) : window.open(offer.ctaHref, '_blank'))}
              >
                {offer.featured && <div style={featuredBadge}>Anbefalt</div>}

                <span style={cardTag}>{offer.tag}</span>
                <h3 style={cardTitle}>{offer.title}</h3>
                <p style={cardOutcome}>{offer.outcome}</p>

                <div style={priceBlock}>
                  <span style={priceValue}>{offer.price}</span>
                  <span style={priceBilling}>{offer.billing}</span>
                </div>

                <div style={fitsBox}>
                  <p style={fitsLabel}>Passer til jer, hvis...</p>
                  <ul style={fitsList}>
                    {offer.fits.map((fit) => (
                      <li key={fit} style={fitsItem}>
                        <span style={arrowIcon}>→</span>
                        <span>{fit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  type="button"
                  style={offer.featured ? ctaBtnPrimary : ctaBtnSecondary}
                  onClick={(e) => {
                    e.stopPropagation();
                    offer.ctaRoute ? navigate(offer.ctaRoute) : window.open(offer.ctaHref, '_blank');
                  }}
                >
                  {offer.ctaLabel}
                </button>
              </article>
          ))}
        </div>
      </section>

      {/* FINDER */}
      <section style={finderSection}>
        <div style={finderBox}>
          <h2 style={finderTitle}>Hvad er jeres situation?</h2>
          <p style={finderSub}>Vælg det, der ligner jer mest, så peger vi på det rigtige udgangspunkt.</p>
          <div style={finderGrid}>
            {finderOptions.map(({ id, Icon, title, desc }) => (
              <button
                key={id}
                type="button"
                style={{
                  ...finderOption,
                  ...(activeFinder === id ? finderOptionActive : {}),
                }}
                onClick={() => setActiveFinder(id)}
              >
                <Icon style={{ fontSize: '1.6rem', color: activeFinder === id ? '#C03626' : '#8C5A50', marginBottom: '0.5rem' }} />
                <strong style={finderOptionTitle}>{title}</strong>
                <p style={finderOptionDesc}>{desc}</p>
              </button>
            ))}
          </div>
          {activeFinder && (
            <div style={finderResult}>
              <strong style={finderResultLabel}>{finderResults[activeFinder].label}</strong>
              <p style={finderResultText}>{finderResults[activeFinder].text}</p>
            </div>
          )}
        </div>
      </section>

      {/* WHY AICE */}
      <section style={whySection}>
        <div style={sectionHeader}>
          <h2 style={sectionTitle}>Hvorfor AICE?</h2>
          <p style={sectionSubtext}>Det, der adskiller os fra andre AI-konsulenter.</p>
        </div>
        <div style={whyGrid}>
          {whyItems.map(({ Icon, title, desc }) => (
            <div key={title} style={whyItem}>
              <div style={whyIconWrap}>
                <Icon style={{ fontSize: '1.5rem', color: '#C03626' }} />
              </div>
              <h3 style={whyTitle}>{title}</h3>
              <p style={whyDesc}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={faqSection}>
        <h2 style={{ ...sectionTitle, textAlign: 'center', marginBottom: '2rem' }}>Typiske spørgsmål</h2>
        <div style={faqList}>
          {faqs.map((item, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={i} style={faqItem}>
                <button
                  type="button"
                  style={faqQuestion}
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                >
                  <span>{item.q}</span>
                  {isOpen
                    ? <MdOutlineRemove style={{ fontSize: '1.3rem', color: '#C03626', flexShrink: 0 }} />
                    : <MdOutlineAdd style={{ fontSize: '1.3rem', color: '#C03626', flexShrink: 0 }} />
                  }
                </button>
                {isOpen && <p style={faqAnswer}>{item.a}</p>}
              </div>
            );
          })}
        </div>
      </section>

      {/* FOOTER CTA */}
      <section style={ctaSection}>
        <h2 style={ctaTitle}>Usikker på, hvad der passer jer?</h2>
        <p style={ctaText}>
          Book et gratis 20-minutters afklaringsmøde. Ingen salgspitch, bare en ærlig snak
          om, hvor I er, og hvad der giver mening.
        </p>
        <a href={MEETING_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <button type="button" style={ctaBtn}>Book et gratis møde</button>
        </a>
      </section>
    </div>
  );
};

export default Ydelser;

// ── Styles ──────────────────────────────────────────────

const pageStyle = {
  background: 'linear-gradient(180deg, #FFF4F0 0%, #FFFFFF 30%)',
  minHeight: '100vh',
  color: '#1f1f1f',
};

const heroSection = {
  maxWidth: '760px',
  margin: '0 auto',
  padding: '5rem clamp(1.5rem, 5vw, 3rem) 3rem',
  textAlign: 'center',
};

const eyebrow = {
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: '#C03626',
  fontSize: '0.85rem',
  marginBottom: '1rem',
};

const heroTitle = {
  fontSize: 'clamp(2rem, 5vw, 3.2rem)',
  fontWeight: 800,
  lineHeight: 1.1,
  margin: '0 0 1rem',
  color: '#1f1f1f',
};

const heroText = {
  fontSize: '1.15rem',
  color: '#5C392F',
  lineHeight: 1.65,
  margin: 0,
};

// Finder
const finderSection = {
  maxWidth: '900px',
  margin: '0 auto 4rem',
  padding: '0 clamp(1rem, 4vw, 3rem)',
};

const finderBox = {
  background: '#fff',
  border: '1.5px solid #F1D5CF',
  borderRadius: '18px',
  padding: 'clamp(1.5rem, 4vw, 2.5rem)',
  boxShadow: '0 8px 32px rgba(192,54,38,0.07)',
};

const finderTitle = {
  fontSize: '1.3rem',
  fontWeight: 700,
  margin: '0 0 0.4rem',
  color: '#1f1f1f',
};

const finderSub = {
  fontSize: '0.97rem',
  color: '#5C392F',
  margin: '0 0 1.5rem',
};

const finderGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1rem',
};

const finderOption = {
  background: '#FFF9F8',
  border: '1.5px solid #F1D5CF',
  borderRadius: '12px',
  padding: '1.25rem',
  cursor: 'pointer',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  transition: 'border-color 0.2s, background 0.2s',
};

const finderOptionActive = {
  borderColor: '#C03626',
  background: '#FFF2EE',
};

const finderOptionTitle = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#1f1f1f',
  display: 'block',
  marginBottom: '0.2rem',
};

const finderOptionDesc = {
  fontSize: '0.85rem',
  color: '#8C5A50',
  lineHeight: 1.45,
  margin: 0,
};

const finderResult = {
  marginTop: '1.25rem',
  padding: '1.1rem 1.4rem',
  background: '#FFF2EE',
  borderLeft: '4px solid #C03626',
  borderRadius: '8px',
};

const finderResultLabel = {
  display: 'block',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#C03626',
  marginBottom: '0.35rem',
};

const finderResultText = {
  fontSize: '0.92rem',
  color: '#5C392F',
  lineHeight: 1.65,
  margin: 0,
};

// Cards
const cardsSection = {
  maxWidth: '1100px',
  margin: '0 auto 5rem',
  padding: '0 clamp(1rem, 4vw, 3rem)',
};

const sectionHeader = {
  marginBottom: '2rem',
  textAlign: 'center',
};

const sectionTitle = {
  fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
  fontWeight: 800,
  margin: '0 0 0.5rem',
  color: '#1f1f1f',
};

const sectionSubtext = {
  fontSize: '1.05rem',
  color: '#5C392F',
  margin: 0,
};

const cardsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '1.5rem',
  alignItems: 'start',
};

const card = {
  background: '#fff',
  border: '1.5px solid #F1D5CF',
  borderRadius: '18px',
  padding: '2rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  cursor: 'pointer',
  boxShadow: '0 8px 24px rgba(192,54,38,0.07)',
  transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
};

const cardFeatured = {
  border: '2px solid #C03626',
  boxShadow: '0 16px 40px rgba(192,54,38,0.14)',
};


const featuredBadge = {
  position: 'absolute',
  top: '-14px',
  left: '50%',
  transform: 'translateX(-50%)',
  background: '#C03626',
  color: '#fff',
  fontSize: '0.75rem',
  fontWeight: 700,
  padding: '3px 14px',
  borderRadius: '20px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const cardTag = {
  display: 'inline-block',
  background: '#FFF2EE',
  color: '#C03626',
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  padding: '4px 10px',
  borderRadius: '20px',
};

const cardTitle = {
  fontSize: '1.5rem',
  fontWeight: 800,
  margin: 0,
  color: '#1f1f1f',
};

const cardOutcome = {
  fontSize: '0.97rem',
  color: '#5C392F',
  lineHeight: 1.6,
  margin: 0,
  fontWeight: 500,
};

const priceBlock = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
};

const priceValue = {
  fontSize: '1.7rem',
  fontWeight: 800,
  color: '#C03626',
  lineHeight: 1,
};

const priceBilling = {
  fontSize: '0.85rem',
  color: '#8C5A50',
};

const fitsBox = {
  background: '#FFF9F8',
  borderRadius: '10px',
  padding: '1rem 1.1rem',
};

const fitsLabel = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#8C5A50',
  margin: '0 0 0.6rem',
};

const fitsList = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
};

const fitsItem = {
  fontSize: '0.88rem',
  color: '#3a1a10',
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'flex-start',
  lineHeight: 1.45,
};

const arrowIcon = {
  color: '#C03626',
  fontWeight: 700,
  flexShrink: 0,
  marginTop: '1px',
};

const divider = {
  height: '1px',
  background: '#F1D5CF',
  margin: '0 -0.25rem',
};

const includesLabel = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#8C5A50',
  margin: 0,
};

const includesList = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const includesItem = {
  fontSize: '0.92rem',
  color: '#3a1a10',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.6rem',
  lineHeight: 1.45,
};

const checkIcon = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  background: '#C03626',
  color: '#fff',
  fontSize: '0.7rem',
  fontWeight: 700,
  flexShrink: 0,
  marginTop: '2px',
};

const ctaBtnPrimary = {
  display: 'block',
  width: '100%',
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  padding: '0.85rem 1.5rem',
  fontSize: '0.97rem',
  fontWeight: 700,
  cursor: 'pointer',
  marginTop: 'auto',
  boxShadow: '0 8px 20px rgba(192,54,38,0.25)',
};

const ctaBtnSecondary = {
  display: 'block',
  width: '100%',
  background: 'transparent',
  color: '#C03626',
  border: '1.5px solid #C03626',
  borderRadius: '10px',
  padding: '0.85rem 1.5rem',
  fontSize: '0.97rem',
  fontWeight: 700,
  cursor: 'pointer',
  marginTop: 'auto',
};

// Why AICE
const whySection = {
  maxWidth: '900px',
  margin: '0 auto 5rem',
  padding: '0 clamp(1rem, 4vw, 3rem)',
};

const whyGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const whyItem = {
  background: '#fff',
  border: '1px solid #F1D5CF',
  borderRadius: '16px',
  padding: '1.5rem',
  boxShadow: '0 6px 18px rgba(192,54,38,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.6rem',
};

const whyIconWrap = {
  width: '44px',
  height: '44px',
  borderRadius: '12px',
  background: '#FFF2EE',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const whyTitle = {
  fontSize: '1rem',
  fontWeight: 700,
  margin: 0,
  color: '#1f1f1f',
};

const whyDesc = {
  fontSize: '0.92rem',
  color: '#5C392F',
  lineHeight: 1.6,
  margin: 0,
};

// FAQ
const faqSection = {
  maxWidth: '720px',
  margin: '0 auto 5rem',
  padding: '0 clamp(1rem, 4vw, 3rem)',
};

const faqList = {
  display: 'flex',
  flexDirection: 'column',
};

const faqItem = {
  borderBottom: '1px solid #F1D5CF',
};

const faqQuestion = {
  width: '100%',
  background: 'none',
  border: 'none',
  padding: '1.25rem 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1f1f1f',
  cursor: 'pointer',
  textAlign: 'left',
};

const faqAnswer = {
  fontSize: '0.95rem',
  color: '#5C392F',
  lineHeight: 1.7,
  margin: '0 0 1.25rem',
};

// Footer CTA
const ctaSection = {
  background: 'linear-gradient(135deg, #C03626 0%, #8a2419 100%)',
  color: '#fff',
  textAlign: 'center',
  padding: 'clamp(3.5rem, 8vw, 6rem) clamp(1.5rem, 5vw, 4rem)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
};

const ctaTitle = {
  fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
  fontWeight: 800,
  margin: 0,
};

const ctaText = {
  fontSize: '1.05rem',
  color: 'rgba(255,255,255,0.8)',
  maxWidth: '520px',
  lineHeight: 1.65,
  margin: 0,
};

const ctaBtn = {
  background: '#fff',
  color: '#C03626',
  border: 'none',
  borderRadius: '10px',
  padding: '1rem 2.5rem',
  fontSize: '1.05rem',
  fontWeight: 700,
  cursor: 'pointer',
  marginTop: '0.5rem',
  boxShadow: '0 10px 28px rgba(0,0,0,0.15)',
};
